/**
 * HDT Lazy Load System
 * Advanced lazy loading for images with beautiful loading animations
 */

class HDTLazyLoad {
  constructor(options = {}) {
    this.options = {
      rootMargin: options.rootMargin || '100px',
      threshold: options.threshold || 0.01,
      loadingClass: 'hdt-lazy-img',
      loadedClass: 'hdt-loaded',
      errorClass: 'hdt-lazy-error',
      wrapperLoadedClass: 'hdt-img-loaded',
      enablePlaceholder: options.enablePlaceholder !== false,
      placeholderType: options.placeholderType || 'skeleton', // 'skeleton', 'spinner', 'blur'
      fadeIn: options.fadeIn !== false,
      retryAttempts: options.retryAttempts || 2,
      retryDelay: options.retryDelay || 1000
    };

    this.observer = null;
    this.images = new Map();
    this.init();
  }

  init() {
    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
      console.warn('Intersection Observer not supported, loading all images immediately');
      this.loadAllImages();
      return;
    }

    this.setupObserver();
    this.observeImages();
    this.setupMutationObserver();
  }

  setupObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
        }
      });
    }, {
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold
    });
  }

  setupMutationObserver() {
    // Watch for dynamically added images
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'IMG' && node.hasAttribute('data-lazy-src')) {
              this.prepareImage(node);
              this.observer.observe(node);
            } else {
              // Check for images within added nodes
              const images = node.querySelectorAll('img[data-lazy-src]');
              images.forEach(img => {
                this.prepareImage(img);
                this.observer.observe(img);
              });
            }
          }
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  observeImages() {
    const images = document.querySelectorAll('img[data-lazy-src], img[loading="lazy"]');
    images.forEach(img => {
      this.prepareImage(img);
      this.observer.observe(img);
    });
  }

  prepareImage(img) {
    // Add loading class
    img.classList.add(this.options.loadingClass);

    // Store retry attempts
    this.images.set(img, { attempts: 0 });

    // Wrap image if not already wrapped
    if (!img.parentElement.classList.contains('hdt-lazy-wrapper')) {
      this.wrapImage(img);
    }
  }

  wrapImage(img) {
    const wrapper = document.createElement('div');
    wrapper.className = 'hdt-lazy-wrapper';
    
    // Copy aspect ratio if exists
    const ratio = img.getAttribute('data-aspect-ratio');
    if (ratio) {
      wrapper.style.aspectRatio = ratio;
    }

    // Add placeholder based on type
    if (this.options.enablePlaceholder) {
      if (this.options.placeholderType === 'skeleton') {
        const skeleton = document.createElement('div');
        skeleton.className = 'hdt-lazy-skeleton';
        wrapper.appendChild(skeleton);
      } else if (this.options.placeholderType === 'spinner') {
        const spinner = document.createElement('div');
        spinner.className = 'hdt-lazy-spinner';
        spinner.innerHTML = '<div class="hdt-lazy-spinner-icon"></div>';
        wrapper.appendChild(spinner);
      } else if (this.options.placeholderType === 'blur') {
        img.classList.add('hdt-lazy-blur');
      }
    }

    // Wrap the image
    img.parentNode.insertBefore(wrapper, img);
    wrapper.appendChild(img);
  }

  loadImage(img) {
    const lazySrc = img.getAttribute('data-lazy-src') || img.getAttribute('src');
    const lazySrcset = img.getAttribute('data-lazy-srcset') || img.getAttribute('srcset');
    
    if (!lazySrc) {
      this.observer.unobserve(img);
      return;
    }

    // Create new image to preload
    const tempImage = new Image();
    
    tempImage.onload = () => {
      this.onImageLoad(img, lazySrc, lazySrcset);
    };

    tempImage.onerror = () => {
      this.onImageError(img, lazySrc, lazySrcset);
    };

    // Set srcset first if available
    if (lazySrcset) {
      tempImage.srcset = lazySrcset;
    }
    tempImage.src = lazySrc;
  }

  onImageLoad(img, src, srcset) {
    // Set the actual image source
    if (srcset) {
      img.srcset = srcset;
    }
    img.src = src;

    // Add loaded class with slight delay for animation
    requestAnimationFrame(() => {
      img.classList.add(this.options.loadedClass);
      
      const wrapper = img.closest('.hdt-lazy-wrapper');
      if (wrapper) {
        wrapper.classList.add(this.options.wrapperLoadedClass);
      }

      // Remove blur class if exists
      img.classList.remove('hdt-lazy-blur');

      // Trigger custom event
      img.dispatchEvent(new CustomEvent('hdtImageLoaded', {
        detail: { src, srcset }
      }));
    });

    // Unobserve the image
    this.observer.unobserve(img);
    this.images.delete(img);
  }

  onImageError(img, src, srcset) {
    const imageData = this.images.get(img);
    
    if (!imageData) return;

    imageData.attempts++;

    // Retry loading
    if (imageData.attempts < this.options.retryAttempts) {
      setTimeout(() => {
        this.loadImage(img);
      }, this.options.retryDelay * imageData.attempts);
    } else {
      // Failed after all retries
      const wrapper = img.closest('.hdt-lazy-wrapper');
      if (wrapper) {
        wrapper.classList.add(this.options.errorClass);
        
        // Add error icon
        const errorIcon = document.createElement('div');
        errorIcon.className = 'hdt-lazy-error-icon';
        errorIcon.innerHTML = '⚠️';
        wrapper.appendChild(errorIcon);
      }

      // Trigger custom event
      img.dispatchEvent(new CustomEvent('hdtImageError', {
        detail: { src, srcset, attempts: imageData.attempts }
      }));

      this.observer.unobserve(img);
      this.images.delete(img);
    }
  }

  loadAllImages() {
    // Fallback for browsers without Intersection Observer
    const images = document.querySelectorAll('img[data-lazy-src]');
    images.forEach(img => {
      const src = img.getAttribute('data-lazy-src');
      const srcset = img.getAttribute('data-lazy-srcset');
      
      if (srcset) img.srcset = srcset;
      if (src) img.src = src;
      
      img.classList.add(this.options.loadedClass);
    });
  }

  // Public method to manually load an image
  load(img) {
    if (img && this.observer) {
      this.loadImage(img);
    }
  }

  // Public method to refresh observer
  refresh() {
    if (this.observer) {
      this.observer.disconnect();
      this.setupObserver();
      this.observeImages();
    }
  }

  // Public method to destroy observer
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.images.clear();
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHDTLazyLoad);
} else {
  initHDTLazyLoad();
}

function initHDTLazyLoad() {
  // Initialize with default options
  window.hdtLazyLoad = new HDTLazyLoad({
    rootMargin: '150px',
    threshold: 0.01,
    placeholderType: 'skeleton', // Options: 'skeleton', 'spinner', 'blur'
    fadeIn: true,
    retryAttempts: 2
  });

  // Expose to global scope for manual control
  window.HDTLazyLoad = HDTLazyLoad;

  // Listen to Shopify theme editor events
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        window.hdtLazyLoad.refresh();
      }, 100);
    });

    document.addEventListener('shopify:section:reorder', () => {
      setTimeout(() => {
        window.hdtLazyLoad.refresh();
      }, 100);
    });
  }

  // Refresh on AJAX cart updates
  document.addEventListener('cart:refresh', () => {
    setTimeout(() => {
      window.hdtLazyLoad.refresh();
    }, 100);
  });
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HDTLazyLoad;
}


